import { Inject, Injectable, InjectionToken } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';

const CLAVE = 'censo-nav-pila';

/** Tipo de la carga de la página: 'navigate' (URL escrita, enlace o pestaña nueva), 'reload' (F5) o 'back_forward'. Inyectable para las pruebas. */
export const TIPO_CARGA_PAGINA = new InjectionToken<() => string>('TIPO_CARGA_PAGINA', {
  providedIn: 'root',
  factory: () => () => {
    try {
      return (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)?.type ?? 'reload';
    } catch {
      return 'reload';
    }
  }
});
const MAXIMO = 30;

function leer(): string[] {
  try {
    const crudo = sessionStorage.getItem(CLAVE);
    const lista = crudo ? JSON.parse(crudo) : [];
    return Array.isArray(lista) ? lista.filter(url => typeof url === 'string' && url.startsWith('/')) : [];
  } catch {
    return [];
  }
}

/**
 * Pila de las páginas de Censo visitadas en esta pestaña (en sessionStorage: sobrevive a F5), para que «Volver» lleve a la
 * página desde la que se llegó (con su búsqueda, filtros y página) y NUNCA fuera de Censo: no se usa history.back(), solo URLs
 * internas del router. Sin página previa (enlace directo o pestaña nueva) se usa el destino por defecto de cada pantalla.
 * Se mantiene coherente con el botón Atrás del navegador (si el destino es la entrada anterior de la pila, se retrocede en ella) y
 * las navegaciones con `replaceUrl` (p. ej. el estado de un listado en la URL) sustituyen la entrada actual en vez de añadir otra.
 */
@Injectable({ providedIn: 'root' })
export class NavegacionService {
  private pila: string[] = leer();
  private reemplazoPendiente = false;

  constructor(private readonly router: Router, @Inject(TIPO_CARGA_PAGINA) tipoCarga: () => string) {
    // Un enlace directo o una URL escrita abren un contexto NUEVO (sin página previa de Censo); F5 y Atrás/Adelante conservan el origen.
    if (tipoCarga() === 'navigate') this.pila = [];
    router.events.subscribe(evento => {
      if (evento instanceof NavigationStart) {
        this.reemplazoPendiente = Boolean(router.getCurrentNavigation()?.extras?.replaceUrl);
      } else if (evento instanceof NavigationEnd) {
        this.registrar(evento.urlAfterRedirects);
      }
    });
  }

  private registrar(url: string): void {
    if (!url.startsWith('/') || url.startsWith('/login')) return;
    const ultima = this.pila.length - 1;
    if (this.pila[ultima] === url) return;
    if (this.reemplazoPendiente && ultima >= 0) {
      this.pila[ultima] = url;
    } else if (this.pila.length >= 2 && this.pila[ultima - 1] === url) {
      this.pila.pop(); // retroceso (Atrás del navegador o «Volver»)
    } else {
      this.pila.push(url);
    }
    if (this.pila.length > MAXIMO) this.pila = this.pila.slice(-MAXIMO);
    this.reemplazoPendiente = false;
    try { sessionStorage.setItem(CLAVE, JSON.stringify(this.pila)); } catch { /* sin almacenamiento: solo se pierde el origen tras F5 */ }
  }

  /** Página de Censo desde la que se llegó a la actual, o null. */
  previa(): string | null {
    return this.pila.length >= 2 ? this.pila[this.pila.length - 2] : null;
  }

  /** «Volver»: a la página previa de Censo (con sus parámetros) o, si no la hay, al destino por defecto. */
  volver(porDefecto: string): Promise<boolean> {
    return this.router.navigateByUrl(this.previa() ?? porDefecto);
  }

  /**
   * «Volver al listado» (p. ej. desde un error): si la página previa ES ese listado (con su búsqueda y página) se vuelve a ella;
   * en cualquier otro caso, al listado a secas. Evita que el botón de un panel de error lleve a una ficha cualquiera.
   */
  volverAlListado(listado: string): Promise<boolean> {
    const previa = this.previa();
    const ruta = (url: string) => url.split(/[?#]/)[0];
    return this.router.navigateByUrl(previa && ruta(previa) === ruta(listado) ? previa : listado);
  }

  /** Solo para pruebas. */
  limpiar(): void {
    this.pila = [];
    try { sessionStorage.removeItem(CLAVE); } catch { /* nada */ }
  }
}
